// Bring-your-own-key vault for AI generation.
//
// The user's OpenAI / Anthropic API key never leaves their browser except in the
// request to that provider:
//   * at rest it is encrypted with AES-256-GCM under a key derived from a passphrase
//     only the user knows (PBKDF2-SHA256, 600k iterations) and stored in this
//     browser's localStorage — Otable's servers, database and admins never receive it;
//   * after unlocking, the plaintext key lives only in this module's memory (never in
//     React state, storage, logs or requests to Otable) and is wiped after 30 minutes
//     of inactivity, on lock, or when the page is closed;
//   * generation requests go directly from the browser to the provider.
//
// Limit: like any browser-side secret, it is only as safe as the JavaScript the page
// runs — a compromised frontend deployment, an XSS bug or a malicious browser
// extension could read it while unlocked.

const STORAGE_PREFIX = 'otable.byok.v1.';
const PBKDF2_ITERATIONS = 600000;
const IDLE_LOCK_MS = 30 * 60 * 1000;

export const PROVIDERS = {
  anthropic: {
    label: 'Anthropic (Claude)',
    models: [
      { id: 'claude-opus-5-5', label: 'Claude Opus 5.5 — best quality' },
      { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 — faster, lower cost' },
    ],
    keyPrefix: 'sk-ant-',
  },
  openai: {
    label: 'OpenAI',
    models: [], // typed by the user: the model name enabled on their OpenAI account
    keyPrefix: 'sk-',
  },
};

let unlocked = null; // { userId, provider, model, apiKey }
let idleTimer = null;

const storageKey = (userId) => `${STORAGE_PREFIX}${userId}`;
const readStored = (userId) => {
  try { return JSON.parse(localStorage.getItem(storageKey(userId)) || 'null'); } catch { return null; }
};

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

const deriveKey = async (passphrase, salt) => {
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

const touch = () => {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(lock, IDLE_LOCK_MS);
};

/** { configured, unlocked, provider, model } for the user — never the key. */
export const status = (userId) => {
  const stored = readStored(userId);
  return {
    configured: Boolean(stored),
    unlocked: Boolean(unlocked && unlocked.userId === String(userId)),
    provider: stored?.provider || null,
    model: stored?.model || null,
  };
};

/** Encrypt and store the key for this user on this device, and unlock it. */
export const saveKey = async (userId, { provider, model, apiKey, passphrase }) => {
  if (!PROVIDERS[provider]) throw new Error('Unknown provider');
  if (!apiKey?.trim() || !model?.trim()) throw new Error('API key and model are required');
  if ((passphrase || '').length < 10) throw new Error('Use a passphrase of at least 10 characters');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(apiKey.trim()));
  localStorage.setItem(storageKey(userId), JSON.stringify({
    v: 1, provider, model: model.trim(), salt: b64(salt), iv: b64(iv), ct: b64(ciphertext),
  }));
  unlocked = { userId: String(userId), provider, model: model.trim(), apiKey: apiKey.trim() };
  touch();
};

/** Decrypt into memory. A wrong passphrase fails AES-GCM authentication. */
export const unlock = async (userId, passphrase) => {
  const stored = readStored(userId);
  if (!stored) throw new Error('No API key saved on this device');
  try {
    const key = await deriveKey(passphrase, unb64(stored.salt));
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(stored.iv) }, key, unb64(stored.ct));
    unlocked = { userId: String(userId), provider: stored.provider, model: stored.model, apiKey: new TextDecoder().decode(plain) };
    touch();
  } catch {
    throw new Error('Wrong passphrase');
  }
};

export function lock() {
  unlocked = null;
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
}

/** Remove the encrypted key from this device. */
export const forgetKey = (userId) => {
  lock();
  localStorage.removeItem(storageKey(userId));
};

// ---- Providers -------------------------------------------------------------------

const anthropicGenerate = async ({ apiKey, model }, { system, user }, onText) => {
  const { default: Anthropic } = await import('@anthropic-ai/sdk');
  // Browser use is intended here: it is the user's own key, sent only to Anthropic
  const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });
  try {
    const stream = client.beta.messages.stream({
      model,
      max_tokens: 64000,
      output_config: { effort: 'high' },
      // A safety decline is re-run server-side on Anthropic's recommended fallback model
      betas: ['server-side-fallback-2026-07-01'],
      fallbacks: 'default',
      system,
      messages: [{ role: 'user', content: user }],
    });
    for await (const event of stream) {
      if (event.type === 'content_block_delta' && event.delta.type === 'text_delta') onText?.(event.delta.text);
    }
    const message = await stream.finalMessage();
    if (message.stop_reason === 'refusal') {
      throw new Error('The model declined to write this section. Try rephrasing the instruction.');
    }
    const text = message.content.filter((b) => b.type === 'text').map((b) => b.text).join('');
    return { text, truncated: message.stop_reason === 'max_tokens' };
  } catch (error) {
    if (error instanceof Anthropic.AuthenticationError) throw new Error('Anthropic rejected the API key. Check it in “My own API key”.');
    if (error instanceof Anthropic.PermissionDeniedError) throw new Error('This API key is not allowed to use the selected model.');
    if (error instanceof Anthropic.RateLimitError) throw new Error('Anthropic rate limit reached. Wait a moment and try again.');
    if (error instanceof Anthropic.BadRequestError) throw new Error(`Anthropic rejected the request: ${error.message}`);
    if (error instanceof Anthropic.APIConnectionError) throw new Error('Could not reach Anthropic. Check your connection.');
    if (error instanceof Anthropic.APIError) throw new Error(`Anthropic error ${error.status}: ${error.message}`);
    throw error;
  }
};

const openaiGenerate = async ({ apiKey, model }, { system, user }) => {
  let res;
  try {
    res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({ model, messages: [{ role: 'system', content: system }, { role: 'user', content: user }] }),
    });
  } catch {
    throw new Error('Could not reach OpenAI. Check your connection.');
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error('OpenAI rejected the API key. Check it in “My own API key”.');
  if (res.status === 429) throw new Error('OpenAI rate limit or quota reached.');
  if (!res.ok) throw new Error(`OpenAI error ${res.status}: ${data?.error?.message || 'request failed'}`);
  const choice = data.choices?.[0];
  return { text: choice?.message?.content || '', truncated: choice?.finish_reason === 'length' };
};

/**
 * Generate with the unlocked key. prompt: { system, user }. onText(chunk) streams
 * partial text where the provider supports it. Returns { text, truncated }.
 */
export const generate = async (userId, prompt, onText) => {
  if (!unlocked || unlocked.userId !== String(userId)) throw new Error('Unlock your API key first');
  touch();
  const creds = { apiKey: unlocked.apiKey, model: unlocked.model };
  const result = unlocked.provider === 'anthropic'
    ? await anthropicGenerate(creds, prompt, onText)
    : await openaiGenerate(creds, prompt);
  touch();
  return result;
};

/**
 * An image from the unlocked OpenAI key (Claude models do not make images), sent straight from the
 * browser to OpenAI. image: { prompt, model, size, quality }. Returns a PNG Blob.
 */
export const generateImage = async (userId, { prompt, model, size, quality }) => {
  if (!unlocked || unlocked.userId !== String(userId)) throw new Error('Unlock your API key first');
  if (unlocked.provider !== 'openai') {
    throw new Error('Claude cannot make images. Use Otable credits, or save an OpenAI key in “My own API key”.');
  }
  touch();
  let res;
  try {
    res = await fetch('https://api.openai.com/v1/images/generations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${unlocked.apiKey}` },
      body: JSON.stringify({ model, prompt, size, quality, n: 1 }),
    });
  } catch {
    throw new Error('Could not reach OpenAI. Check your connection.');
  }
  const data = await res.json().catch(() => ({}));
  touch();
  if (res.status === 401) throw new Error('OpenAI rejected the API key. Check it in “My own API key”.');
  if (res.status === 429) throw new Error('OpenAI rate limit or quota reached.');
  if (!res.ok) throw new Error(`OpenAI error ${res.status}: ${data?.error?.message || 'request failed'}`);
  const b64 = data.data?.[0]?.b64_json;
  if (!b64) throw new Error('OpenAI returned no image.');
  return new Blob([unb64(b64)], { type: 'image/png' });
};
